"use client";

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CalendarDays,
  MapPin,
  Plus,
  Building2,
  Search,
  Building,
  Calendar,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  ArrowUpDown,
} from "lucide-react";
import HActionButton from "src/hcomponents/HActionButton";
import HHeader from "src/hcomponents/HHeader";
import HPageContainer from "src/hcomponents/HPageContainer";
import HTable from "src/hcomponents/HTable";
import HStatsCard from "src/hcomponents/HStatsCard";
import { useHistory } from "react-router-dom";
import { showApiMessage } from "../../../utils/messageHandler";

// Import new components
import ViewToggle from "./components/ViewToggle";
import GridView from "./components/GridView";
import ReusableMapView from "../../../components/ReusableMapView";
import HEmptyState from "../../../hcomponents/HEmptyState";
import HPagination from "../../../hcomponents/HPagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../shadcn/ui/dialog";
import { Button } from "../../../shadcn/ui/button";
import { Badge } from "../../../shadcn/ui/badge";
import { SlidersHorizontal } from "lucide-react";
import IllustrativeFilterDialog from "../../../components/IllustrativeFilterDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../shadcn/ui/dropdown-menu";

// Import the spaces service
import spaceManagementService from "../../../services/host-apiServices/spaceManagementService";
import { getUser } from "../../../services/localService";
import { generateSpaceName, generateLocationString } from "../../../utils/spaceNamingUtils";
import { formatDurationType } from "../../../utils/bookingTimeframeUtils";
import { useUserCurrency } from "../../../hooks/useUserCurrency";
import logger from "../../../utils/logger";


// Component for space image with fallback icon
const SpaceImageWithFallback = ({ space, size = "sm", className = "" }) => {
  const [hasError, setHasError] = useState(false);

  // Size variants for different use cases
  const sizeClasses = {
    xs: "w-6 h-6", // 24px
    sm: "w-8 h-8 sm:w-10 sm:h-10", // 32px/40px
    md: "w-12 h-12 sm:w-16 sm:h-16", // 48px/64px
    lg: "w-20 h-20 sm:w-24 sm:h-24", // 80px/96px
    xl: "w-32 h-32", // 128px
  };

  const iconSizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4 sm:w-5 sm:h-5",
    md: "w-6 h-6 sm:w-8 sm:h-8",
    lg: "w-10 h-10 sm:w-12 sm:h-12",
    xl: "w-16 h-16",
  };

  if (!space.image || hasError) {
    return (
      <div
        className={`flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg ${sizeClasses[size]} ${className}`}
      >
        <Building2 className={`text-blue-400 ${iconSizes[size]}`} />
      </div>
    );
  }

  return (
    <img
      src={space.image}
      alt={space.name}
      className={`object-cover flex-shrink-0 rounded-lg ${sizeClasses[size]} ${className}`}
      onError={() => setHasError(true)}
    />
  );
};

const HostSpaces = ({ _onAddWorkspace, _onAddMeetingRoom, categoryId, showHeader = true }) => {
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const isPlayView = (urlParams.get("type") || "").toLowerCase() === "play";
  const userCurrency = useUserCurrency();

  // Initialize pagination state from URL parameters with defaults
  const getInitialPage = () => {
    const pageParam = urlParams.get("page");
    return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0; // Convert 1-based to 0-based
  };

  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // 'list', 'grid', or 'map'
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const pageSize = 20; // Fixed page size
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [bookingStatistics, setBookingStatistics] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [spaceToDelete, setSpaceToDelete] = useState(null);
  const [spaceToPublish, setSpaceToPublish] = useState(null);
  const [publishAction, setPublishAction] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterState, setFilterState] = useState({
    priceRange: [0, 10000],
    capacity: 1,
    amenities: [],
    publishedStatus: [],
    featuredStatus: [],
    topSpaces: false,
    country: "",
    categoryId: null,
  });
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortBy, setSortBy] = useState(""); // Default to no explicit sort

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch data when page loads or filters change (NOT when view mode changes)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Only show full page loading on initial load (when pageData is null)
        // For subsequent searches/filters, only show spaces loading
        if (pageData === null) {
          setLoading(true);
        } else {
          setSpacesLoading(true);
        }
        setError(null);

        const currentUser = getUser();
        if (!currentUser) {
          setError("No user found. Please log in again.");
          setLoading(false);
          return;
        }

        // Determine whether to use search or regular spaces endpoint
        const hasSearchQuery = debouncedSearchQuery.trim().length > 0;
        const hasFilters = Object.keys(filters).length > 0;

        logger.log("Fetch conditions:", {
          hasSearchQuery,
          hasFilters,
          filters,
          debouncedSearchQuery,
        });

        // Fetch spaces and statistics in parallel
        let spacesPromise;
        let statisticsPromise;
        let bookingStatsPromise;

        // Always use search endpoint when there are filters, or when there's a search query, or when categoryId is provided
        if (hasSearchQuery || hasFilters || categoryId) {
          const searchParams = {
            pageNumber: currentPage, // Spring data expects 0-based page index
            pageSize,
            "sort.sorted": true,
          };

          // Add sort parameter if selected
          if (sortBy) {
            const [sortField, sortDirection] = sortBy.split(":");
            searchParams.sort = `${sortField},${sortDirection}`;
          }

          // Add search query if present
          if (hasSearchQuery) {
            searchParams.spaceName = debouncedSearchQuery;
            searchParams.addressContains = debouncedSearchQuery;
          }

          // Add comprehensive filter parameters from IllustrativeFilterDialog
          if (filters.minPrice !== undefined) {
            searchParams.minPrice = filters.minPrice;
          }
          if (filters.maxPrice !== undefined) {
            searchParams.maxPrice = filters.maxPrice;
          }
          if (filters.minCapacity !== undefined) {
            searchParams.minCapacity = filters.minCapacity;
          }
          if (filters.amenityIds) {
            searchParams.amenityIds = filters.amenityIds;
          }
          if (filters.featured !== undefined) {
            // Only include featured parameter when it's true, or explicitly set to false for NOT_FEATURED
            searchParams.featured = filters.featured;
          }
          if (filters.topSpaces !== undefined) {
            searchParams.topSpaces = filters.topSpaces;
          }
          if (filters.country) {
            searchParams.country = filters.country;
          }
          if (filters.status) {
            searchParams.status = filters.status;
          }

          // Add categoryId filter - prioritize filters from dialog over props
          const finalCategoryId = filters.categoryId || categoryId;
          if (finalCategoryId) {
            searchParams.categoryId = finalCategoryId;
            logger.log("🔍 CategoryId being passed:", finalCategoryId);
          }

          logger.log("Search API called with params:", searchParams);
          spacesPromise = spaceManagementService.searchSpaces(searchParams);
        } else {
          // Use regular getUserSpaces when no search query and no filters
          const userSpacesParams = {
            pageNumber: currentPage, // Use 0-based page index for API
            pageSize: pageSize,
          };

          // Add sort parameters if selected
          if (sortBy) {
            const [sortField, sortDirection] = sortBy.split(":");
            userSpacesParams.sortBy = sortField;
            userSpacesParams.sortOrder = sortDirection;
          }

          spacesPromise = spaceManagementService.getUserSpaces(currentUser.id, userSpacesParams);
        }

        // Fetch statistics only on initial load
        if (pageData === null) {
          statisticsPromise = spaceManagementService.getSpaceStatistics(currentUser.id);
          bookingStatsPromise = spaceManagementService.getBookingStatistics(new Date().getFullYear()).catch(() => null);
        }

        // Wait for all promises
        const [spacesResponse, statisticsResponse, bookingStatsResponse] = await Promise.all([
          spacesPromise,
          statisticsPromise || Promise.resolve(null),
          bookingStatsPromise || Promise.resolve(null),
        ]);

        if (spacesResponse.status === 200) {
          const spacesData = spacesResponse.data;

          // Transform API response to match existing UI structure
          const transformedSpaces = (spacesData.content || []).map(space => {
            // Get the first instance for location and title info
            const firstInstance = space.instances?.[0];

            // Use spaceNamingUtils for consistent naming
            const hostOrgName = currentUser?.organization?.name || currentUser?.organizationName;
            const spaceName = generateSpaceName(
              { ...space, organizationName: space.organizationName || hostOrgName },
              firstInstance,
              true
            );
            const locationString = generateLocationString(space, firstInstance);

            // Extract image from the API response structure - use media.images
            let imageUrl = null;
            if (
              space.media &&
              space.media.images &&
              Array.isArray(space.media.images) &&
              space.media.images.length > 0
            ) {
              const validImage = space.media.images.find(img => img && typeof img === "string" && img.trim() !== "");
              if (validImage) {
                imageUrl = validImage;
              }
            }
            // Fallback to space.images if media.images is not available
            if (!imageUrl && space.images && Array.isArray(space.images) && space.images.length > 0) {
              const validImage = space.images.find(img => img && typeof img === "string" && img.trim() !== "");
              if (validImage) {
                imageUrl = validImage;
              }
            }

            return {
              // Keep original API fields
              ...space,
              // Add compatibility fields for existing UI components
              name: spaceName,
              propertyTitle: spaceName,
              image: imageUrl || "/img/placeholder-space.jpg",
              status: space.status || "UNPUBLISHED",
              propertyStatus: space.status || "UNPUBLISHED",
              type: space.spaceType || "HIGH",
              propertyType: space.spaceType || "HIGH",
              typeOfWorkspace: space.spaceType || "HIGH",
              capacity: space.capacity || 1,
              pricing: space.ratePrice
                ? `${userCurrency} ${(space.ratePrice || 0).toLocaleString()} ${formatDurationType(space.duration || "hour")}`
                : "Price not set",
              location: locationString,
              date: firstInstance?.createdAt ? new Date(firstInstance.createdAt).toLocaleDateString() : "N/A",
              createdDate: firstInstance?.createdAt,
              // Add unit count for UI display
              instanceCount: space.availableInstancesCount || space.instances?.length || 0,
              // Add coordinates for map display
              coordinates: (() => {
                const buildingAddress = firstInstance?.location?.buildingAddress;
                if (buildingAddress?.latitude && buildingAddress?.longitude) {
                  return {
                    latitude: buildingAddress.latitude,
                    longitude: buildingAddress.longitude,
                  };
                }
                return firstInstance?.location?.coordinates || null;
              })(),
              // Add media structure for compatibility
              media: {
                images: space.media?.images?.map(url => ({ url })) || space.images?.map(url => ({ url })) || [],
              },
            };
          });

          // Extract unique values for filters
          const locations = [...new Set(transformedSpaces.map(space => space.location).filter(Boolean))];

          // Use hardcoded space types that match add-space form
          const spaceTypes = [
            { value: "HIGH", label: "High Priority Space" },
            { value: "MEDIUM", label: "Medium Priority Space" },
            { value: "LOW", label: "Low Priority Space" },
          ];

          const transformedData = {
            spaces: transformedSpaces,
            filters: {
              locations,
              spaceTypes,
              statuses: ["PUBLISHED", "UNPUBLISHED"],
            },
            view: viewMode,
          };

          logger.log("Space types for filter dropdown:", spaceTypes);
          setPageData(transformedData);
          const newTotalPages = spacesData.totalPages || 0;
          setTotalPages(newTotalPages);

          // Ensure current page is valid when total pages change
          if (currentPage >= newTotalPages && newTotalPages > 0) {
            const validPage = Math.max(0, newTotalPages - 1);
            setCurrentPage(validPage);
            updateUrlParams(validPage, pageSize);
          }
        } else {
          setError("Failed to fetch spaces data");
        }

        // Set statistics if available
        if (statisticsResponse && statisticsResponse.status === 200) {
          setStatistics(statisticsResponse.data);
          setStatisticsLoading(false);
        }

        // Set booking statistics if available
        if (bookingStatsResponse && bookingStatsResponse.status === 200) {
          setBookingStatistics(bookingStatsResponse.data);
        }

        setLoading(false);
        setSpacesLoading(false);
      } catch (error) {
        logger.error("Error fetching spaces data:", error);
        setError(error.message || "An error occurred while fetching spaces");
        setLoading(false);
        setSpacesLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, pageSize, debouncedSearchQuery, categoryId, sortBy]); // Add sortBy to dependencies

  // Initialize status filter from URL on mount

  // Update URL parameters when pagination changes
  const updateUrlParams = page => {
    const newParams = new URLSearchParams(location.search);

    // Update page (convert 0-based to 1-based for URL)
    if (page === 0) {
      newParams.delete("page");
    } else {
      newParams.set("page", (page + 1).toString());
    }

    // Keep other existing params
    const newSearch = newParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
    history.replace(newUrl);
  };

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    updateUrlParams(newPage);
  };

  const handleSelectAll = checked => {
    if (checked && pageData?.spaces) {
      setSelectedSpaces(pageData.spaces.map(space => space.id));
    } else {
      setSelectedSpaces([]);
    }
  };

  const handleSelectSpace = (space, checked) => {
    if (checked) {
      setSelectedSpaces(prev => [...prev, space.id]);
    } else {
      setSelectedSpaces(prev => prev.filter(id => id !== space.id));
    }
  };

  const handleAddNewSpace = () => {
    if (isPlayView) {
      history.push("/host/play-services/create");
    } else {
      history.push("/host/add-space");
    }
  };

  const handleEditSpace = space => {
    try {
      if (isPlayView) {
        history.push(`/host/play-services/edit/${space.id}`);
        return;
      }
      const category = (space.category || space?.subCategory?.category || "").toString().toUpperCase();
      if (category === "PLAY") {
        history.push(`/host/play-services/edit/${space.id}`);
      } else {
        history.push(`/host/add-space/${space.id}`);
      }
    } catch (_e) {
      history.push(`/host/add-space/${space.id}`);
    }
  };

  const handleDeleteSpace = space => {
    setSpaceToDelete(space);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSpace = async () => {
    if (!spaceToDelete) return;

    try {
      setSpacesLoading(true);
      const response = await spaceManagementService.deleteSpace(spaceToDelete.id);
      if (response.status === 200) {
        showApiMessage(response, "delete space");
        // Refresh the data
        setCurrentPage(0);
        // Trigger a refetch by updating a dummy state
        setFilters(prev => ({ ...prev }));
      } else {
        showApiMessage(response, "delete space");
      }
    } catch (error) {
      logger.error("Error deleting space:", error);
      showApiMessage(error, "delete space");
    } finally {
      setSpacesLoading(false);
      setShowDeleteConfirm(false);
      setSpaceToDelete(null);
    }
  };

  const handleTogglePublishStatus = space => {
    const newStatus = space.status === "PUBLISHED" ? "UNPUBLISHED" : "PUBLISHED";
    const action = newStatus === "PUBLISHED" ? "publish" : "unpublish";

    setSpaceToPublish(space);
    setPublishAction(action);
    setShowPublishConfirm(true);
  };

  const confirmTogglePublishStatus = async () => {
    if (!spaceToPublish) return;

    const newStatus = spaceToPublish.status === "PUBLISHED" ? "UNPUBLISHED" : "PUBLISHED";
    const action = newStatus === "PUBLISHED" ? "publish" : "unpublish";

    try {
      setSpacesLoading(true);
      const response =
        newStatus === "PUBLISHED"
          ? await spaceManagementService.publishSpace(spaceToPublish.id, 0)
          : await spaceManagementService.unpublishSpace(spaceToPublish.id, 0);
      if (response.status === 200) {
        showApiMessage(response, `${action} space`);
        // Refresh the data
        setCurrentPage(0);
        // Trigger a refetch by updating a dummy state
        setFilters(prev => ({ ...prev }));
      } else {
        showApiMessage(response, `${action} space`);
      }
    } catch (error) {
      logger.error(`Error ${action}ing space:`, error);
      showApiMessage(error, `${action} space`);
    } finally {
      setSpacesLoading(false);
      setShowPublishConfirm(false);
      setSpaceToPublish(null);
      setPublishAction("");
    }
  };

  // Filter Handlers
  const applyFilters = dialogFilters => {
    logger.log("🎨 Applying filters from dialog:", dialogFilters);

    // Update local filterState
    setFilterState(dialogFilters);

    const newFilters = {};
    const newActiveFilters = [];

    // Apply price range
    if (dialogFilters.priceRange[0] > 0 || dialogFilters.priceRange[1] < 10000) {
      newFilters.minPrice = dialogFilters.priceRange[0];
      newFilters.maxPrice = dialogFilters.priceRange[1];
      newActiveFilters.push({
        id: "priceRange",
        label: `KES${dialogFilters.priceRange[0]} - KES${dialogFilters.priceRange[1]}`,
        value: `${dialogFilters.priceRange[0]}-${dialogFilters.priceRange[1]}`,
      });
    }

    // Apply capacity
    if (dialogFilters.capacity > 1) {
      newFilters.minCapacity = dialogFilters.capacity;
      newActiveFilters.push({
        id: "capacity",
        label: `Min ${dialogFilters.capacity} people`,
        value: dialogFilters.capacity.toString(),
      });
    }

    // Apply amenities
    if (dialogFilters.amenities.length > 0) {
      newFilters.amenityIds = dialogFilters.amenities;
      newActiveFilters.push({
        id: "amenities",
        label: `${dialogFilters.amenities.length} amenities`,
        value: dialogFilters.amenities.join(","),
      });
    }

    // Apply published status
    if (dialogFilters.publishedStatus.length > 0) {
      newFilters.status = dialogFilters.publishedStatus[0];
      newActiveFilters.push({
        id: "publishedStatus",
        label: dialogFilters.publishedStatus[0],
        value: dialogFilters.publishedStatus[0],
      });
    }

    // Apply featured status
    if (dialogFilters.featuredStatus.length > 0) {
      const isFeatured = dialogFilters.featuredStatus[0] === "FEATURED";
      newFilters.featured = isFeatured;
      newActiveFilters.push({
        id: "featuredStatus",
        label: isFeatured ? "Featured" : "Not Featured",
        value: dialogFilters.featuredStatus[0],
      });
    }

    // Apply top spaces
    if (dialogFilters.topSpaces) {
      newFilters.topSpaces = true;
      newActiveFilters.push({
        id: "topSpaces",
        label: "Top Spaces",
        value: "true",
      });
    }

    // Apply country
    if (dialogFilters.country) {
      newFilters.country = dialogFilters.country;
      newActiveFilters.push({
        id: "country",
        label: dialogFilters.country,
        value: dialogFilters.country,
      });
    }

    // Apply categoryId
    if (dialogFilters.categoryId) {
      newFilters.categoryId = dialogFilters.categoryId;
      newActiveFilters.push({
        id: "categoryId",
        label: dialogFilters.categoryId === 1 ? "Work" : dialogFilters.categoryId === 2 ? "Stay" : "Play",
        value: dialogFilters.categoryId.toString(),
      });
    }

    logger.log("✅ New filters:", newFilters);
    logger.log("✅ Active filter badges:", newActiveFilters);

    setFilters(newFilters);
    setActiveFilters(newActiveFilters);
    setShowFilters(false);
    setCurrentPage(0);
  };

  const clearFilter = filterId => {
    const filter = activeFilters.find(f => f.id === filterId);
    if (filter) {
      let newFilters = { ...filters };

      // Remove the specific filter based on its ID
      if (filter.id === "publishedStatus") {
        delete newFilters.status;
      } else if (filter.id === "featuredStatus") {
        delete newFilters.featured;
      } else if (filter.id === "topSpaces") {
        delete newFilters.topSpaces;
      } else if (filter.id === "priceRange") {
        delete newFilters.minPrice;
        delete newFilters.maxPrice;
      } else if (filter.id === "capacity") {
        delete newFilters.minCapacity;
      } else if (filter.id === "amenities") {
        delete newFilters.amenityIds;
      } else if (filter.id === "country") {
        delete newFilters.country;
      } else if (filter.id === "categoryId") {
        delete newFilters.categoryId;
      }

      setFilters(newFilters);
      setActiveFilters(activeFilters.filter(f => f.id !== filterId));
    }
  };

  const clearAllFilters = () => {
    setActiveFilters([]);
    setFilters({});
    setFilterState({
      priceRange: [0, 10000],
      capacity: 1,
      amenities: [],
      publishedStatus: [],
      featuredStatus: [],
      topSpaces: false,
      country: "",
      categoryId: null,
    });
    setSortBy(""); // Reset sort to default (none)
  };

  // Define table columns for spaces
  const columns = [
    {
      key: "id",
      header: "Space ID",
      sortable: true,
      width: "w-[120px] min-w-[120px]",
      render: space => `#${space.id}`,
    },
    {
      key: "name",
      header: "Space Name",
      sortable: true,
      width: "w-[220px] min-w-[220px]",
      render: space => (
        <div className="flex gap-3 items-center">
          <SpaceImageWithFallback space={space} />
          <span className="font-medium text-gray-900 truncate">{space.name || "Untitled Space"}</span>
        </div>
      ),
    },
    {
      key: "location",
      header: "Location",
      sortable: true,
      render: space => (
        <div className="flex gap-2 items-center text-gray-600">
          <MapPin className="flex-shrink-0 w-4 h-4 text-gray-400" />
          <span className="truncate">{space.location || "No address"}</span>
        </div>
      ),
    },
    {
      key: "instances",
      header: "Units",
      sortable: true,
      width: "w-[120px] min-w-[120px]",
      render: space => (
        <div className="flex gap-2 items-center">
          <Building2 className="flex-shrink-0 w-4 h-4 text-blue-500" />
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {space.instanceCount || 0}
          </span>
        </div>
      ),
    },
    {
      key: "date",
      header: "Date Published",
      sortable: true,
      width: "w-[160px] min-w-[160px]",
      render: space => (
        <div className="flex gap-2 items-center text-gray-600">
          <CalendarDays className="flex-shrink-0 w-4 h-4 text-gray-400" />
          <span className="text-sm">{space.date}</span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "w-[100px] min-w-[100px]",
      render: space => {
        const status = space.status || "UNPUBLISHED";
        const getStatusStyle = status => {
          switch (status.toUpperCase()) {
            case "PUBLISHED":
              return "bg-green-100 text-green-700";
            case "UNPUBLISHED":
              return "bg-orange-100 text-orange-600";
            case "DRAFT":
              return "bg-orange-100 text-orange-600"; // treat as UNPUBLISHED for legacy
            default:
              return "bg-gray-100 text-gray-600";
          }
        };

        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${getStatusStyle(status)}`}
          >
            {(status === "DRAFT" ? "Unpublished" : status).charAt(0).toUpperCase() +
              (status === "DRAFT" ? "Unpublished" : status).slice(1).toLowerCase()}
          </span>
        );
      },
    },
  ];

  // Render loading state
  if (loading) {
    return (
      <HPageContainer>
        {showHeader && <HHeader />}
        <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain max-w-full">
          <div className="space-y-6 animate-pulse">
            {/* Filter placeholders */}
            <div className="flex flex-wrap gap-2 mb-6 sm:gap-4">
              <div className="w-36 h-10 rounded-md bg-slate-200"></div>
              <div className="w-36 h-10 rounded-md bg-slate-200"></div>
              <div className="w-36 h-10 rounded-md bg-slate-200"></div>
            </div>

            {/* Stats placeholders */}
            <div className="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="h-24 rounded-lg bg-slate-200"></div>
              <div className="h-24 rounded-lg bg-slate-200"></div>
              <div className="h-24 rounded-lg bg-slate-200"></div>
              <div className="h-24 rounded-lg bg-slate-200"></div>
            </div>

            {/* Content placeholder */}
            <div className="h-96 rounded-lg bg-slate-200"></div>
          </div>
        </div>
      </HPageContainer>
    );
  }

  // Render error state
  if (error) {
    return (
      <HPageContainer>
        {showHeader && <HHeader />}
        <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
          <div className="flex flex-col justify-center items-center h-96 text-center">
            <div className="p-6 max-w-md bg-red-50 rounded-lg border border-red-200">
              <h3 className="mb-2 text-lg font-medium text-red-800">Error Loading Spaces</h3>
              <p className="mb-4 text-red-600">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-white bg-red-600 rounded-md transition-colors hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </HPageContainer>
    );
  }

  // Render the appropriate view based on viewMode
  const renderView = () => {
    // Show loading state for spaces only
    if (spacesLoading) {
      return (
        <div className="space-y-4 animate-pulse">
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-64 rounded-lg bg-slate-200"></div>
              ))}
            </div>
          ) : viewMode === "map" ? (
            <div className="h-96 rounded-lg bg-slate-200"></div>
          ) : (
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-16 rounded-lg bg-slate-200"></div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (!pageData?.spaces) return null;

    // If no spaces, show empty state for all views
    if (pageData.spaces.length === 0) {
      const hasFiltersOrSearch = Object.keys(filters).length > 0 || debouncedSearchQuery.trim().length > 0;

      return (
        <HEmptyState
          icon={Building2}
          title={hasFiltersOrSearch ? "No Spaces Found" : "No Spaces Yet"}
          description={
            hasFiltersOrSearch
              ? "No spaces match your current search or filters. Try adjusting your criteria."
              : "Add your first space to get started with hosting."
          }
          actionLabel={hasFiltersOrSearch ? "Clear Filters" : "Add Space"}
          onAction={
            hasFiltersOrSearch
              ? () => {
                  setFilters({});
                  setSearchQuery("");
                  setDebouncedSearchQuery("");
                }
              : handleAddNewSpace
          }
          className="h-64"
        />
      );
    }

    switch (viewMode) {
      case "grid":
        return (
          <GridView
            spaces={pageData.spaces}
            onSelectSpace={handleEditSpace}
            onEdit={handleEditSpace}
            onDelete={handleDeleteSpace}
            onTogglePublish={handleTogglePublishStatus}
          />
        );
      case "map":
        return (
          <div className="h-[600px] w-full mb-8">
            <ReusableMapView spaces={pageData.spaces} onSpaceClick={handleEditSpace} />
          </div>
        );
      case "list":
      default:
        return (
          <HTable
            data={pageData.spaces}
            columns={columns}
            selectedItems={selectedSpaces}
            onSelectAll={handleSelectAll}
            onSelectItem={handleSelectSpace}
            showSelectAll={true}
            showActions={true}
            onRowClick={handleEditSpace}
            onDelete={handleDeleteSpace}
          />
        );
    }
  };

  return (
    <>
      <HPageContainer>
        {showHeader && (
          <HHeader
            rightButton={
              <HActionButton
                icon={<Plus className="w-4 h-4" />}
                label="Add New Space"
                onClick={handleAddNewSpace}
                className="text-xs md:text-sm"
              />
            }
          />
        )}

        <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
          {/* Search and Filters Section - Always show */}
          <div className="mb-6 space-y-4" data-tour="spaces-header">
            {/* Search Input */}
            <div className="relative">
              <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search spaces..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="block py-2 pr-12 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              {spacesLoading && (
                <div className="flex absolute inset-y-0 right-0 items-center pr-3">
                  <div className="w-4 h-4 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
                </div>
              )}
              {searchQuery !== debouncedSearchQuery && searchQuery.length > 0 && !spacesLoading && (
                <div className="flex absolute inset-y-0 right-0 items-center pr-3">
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                </div>
              )}
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 gap-2 mb-6 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4" data-tour="spaces-stats">
            <HStatsCard
              title="Total Listings"
              value={(statistics?.totalSpaces || pageData?.spaces?.length || 0).toString()}
              details={<span className="flex gap-1 items-center text-gray-500">All your registered spaces</span>}
              detailsColor="text-gray-500"
              icon={<Building className="w-5 h-5" />}
              isLoading={statisticsLoading}
            />
            <HStatsCard
              title="Available Listings"
              value={(statistics?.availableSpaces || 0).toString()}
              details={
                <span className="flex gap-1 items-center text-gray-500">
                  {statistics?.availabilityPercentage
                    ? `${statistics.availabilityPercentage.toFixed(1)}% available`
                    : "Ready to book"}
                </span>
              }
              detailsColor="text-gray-500"
              icon={<Building2 className="w-5 h-5" />}
              isLoading={statisticsLoading}
            />
            <HStatsCard
              title="Active Bookings"
              value={(bookingStatistics?.totalActiveBookings || 0).toString()}
              details={
                <span className="flex gap-1 items-center">
                  {bookingStatistics?.activeChangeType === "INCREASE" ? (
                    <ArrowUp className="w-4 h-4 text-green-500" />
                  ) : bookingStatistics?.activeChangeType === "DECREASE" ? (
                    <ArrowDown className="w-4 h-4 text-red-500" />
                  ) : null}
                  {bookingStatistics?.activeBookingsChange > 0 && bookingStatistics?.activeChangeType === "INCREASE"
                    ? "+"
                    : ""}
                  {bookingStatistics?.activeBookingsChange || 0} vs last period
                </span>
              }
              detailsColor="text-gray-500"
              icon={<Calendar className="w-5 h-5" />}
              isLoading={statisticsLoading}
            />
            <HStatsCard
              title="Occupancy Rate"
              value={`${(bookingStatistics?.percentageOccupancyRate || statistics?.occupancyRate || 0).toFixed(1)}%`}
              details={
                <span className="flex gap-1 items-center">
                  {bookingStatistics?.occupancyChangeType === "INCREASE" ? (
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  ) : bookingStatistics?.occupancyChangeType === "DECREASE" ? (
                    <ArrowDown className="w-4 h-4 text-red-500" />
                  ) : null}
                  {bookingStatistics?.occupancyRateChange > 0 && bookingStatistics?.occupancyChangeType === "INCREASE"
                    ? "+"
                    : ""}
                  {(bookingStatistics?.occupancyRateChange || 0).toFixed(1)}% vs last period
                </span>
              }
              detailsColor="text-gray-500"
              icon={<TrendingUp className="w-5 h-5" />}
              isLoading={statisticsLoading}
            />
          </div>

          {/* Filters and View Toggle Section - Always show */}
          <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:justify-between sm:items-center sm:gap-4">
            <ViewToggle activeView={viewMode} onViewChange={setViewMode} availableViews={["grid", "list", "map"]} />

            <div className="flex gap-2 items-center">
              {/* Sort Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="bg-white hover:bg-gray-50">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Sort by
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onClick={() => setSortBy("ratePrice:desc")}
                    className={sortBy === "ratePrice:desc" ? "bg-gray-100" : ""}
                  >
                    <ArrowDown className="w-4 h-4 mr-2" />
                    Price - High to Low
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSortBy("ratePrice:asc")}
                    className={sortBy === "ratePrice:asc" ? "bg-gray-100" : ""}
                  >
                    <ArrowUp className="w-4 h-4 mr-2" />
                    Price - Low to High
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(true)}
                className="bg-white hover:bg-gray-50"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {activeFilters.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-white text-gray-900 border border-gray-300">
                    {activeFilters.length}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {/* Active Filters Display - Always show if there are filters */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-2">
              {activeFilters.map(filter => (
                <Badge
                  key={filter.id}
                  variant="secondary"
                  className="px-3 py-1 bg-white text-gray-900 border border-gray-300"
                >
                  {filter.label}
                  <button onClick={() => clearFilter(filter.id)} className="ml-2 hover:text-destructive">
                    ×
                  </button>
                </Badge>
              ))}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-auto py-1">
                Clear all
              </Button>
            </div>
          )}

          {/* Main Content View */}
          {pageData && (
            <>
              <div className="pb-12">{renderView()}</div>

              {/* Pagination Controls - Show for all view modes (grid, map, list) */}
              {pageData?.spaces && pageData.spaces.length > 0 && (
                <HPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              )}
            </>
          )}
        </div>
      </HPageContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Space</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete "{spaceToDelete?.name}"? This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setSpaceToDelete(null);
              }}
              disabled={spacesLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteSpace}
              disabled={spacesLoading}
              className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white"
            >
              {spacesLoading ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{publishAction === "publish" ? "Publish Space" : "Unpublish Space"}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to {publishAction} "{spaceToPublish?.name}"?
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowPublishConfirm(false);
                setSpaceToPublish(null);
                setPublishAction("");
              }}
              disabled={spacesLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmTogglePublishStatus}
              disabled={spacesLoading}
              className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white"
            >
              {spacesLoading
                ? `${publishAction === "publish" ? "Publishing" : "Unpublishing"}...`
                : publishAction === "publish"
                  ? "Publish"
                  : "Unpublish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IllustrativeFilterDialog */}
      <IllustrativeFilterDialog
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={applyFilters}
        onClearFilters={clearAllFilters}
        initialFilters={filterState}
        totalResults={pageData?.totalElements || 0}
        showPublishedStatusFilter={true}
      />
    </>
  );
};

export default HostSpaces;
